import React from 'react';
import { LocalizedMessage } from 'intl/LocalizedComponents';
import _isEmpty from 'lodash/isEmpty';
import _map from 'lodash/map';
import classnames from 'classnames';
import PropTypes from 'prop-types';
// Core components
import TableRow from 'components/core/table/TableRow';
import TableCell from 'components/core/table/TableCell';
import Chip from 'components/core/Chip';
import { IconButton } from 'components/core/Button';
import Tooltip from 'components/core/Tooltip';
// Models
import mCustomer from 'models/cpm/customer';
import mParking from 'models/cpm/parking';
// Permissions
import permissions from 'constants/Permissions';
import OnlyWith from 'components/permissions/OnlyWith';
// Custom
import AppIcon from 'components/icons/AppIcon';
import { listRowSpecsShape, cpmCustomerDataShape } from 'constants/PropTypesShapes';
import {
  AddRightIconButton,
  EditIconButton,
  DeleteIconButton,
  AddBookingIconButton,
} from 'components/buttons/IconButtons';
import TruncatedTooltipText from 'components/misc/TruncatedTooltipText';
import ParkingCategoryDesc from 'cpmCarparks/assets/parkingCategories/ParkingCategoryDesc';
import ParkingStatusInfoIcon from 'cpmCarparks/assets/parkings/ParkingStatusInfoIcon';
import BookingStatusInfoIcon from 'cpmCarparks/assets/bookings/BookingStatusInfoIcon';
import CustomerTableCellItem from 'cpmCarparks/assets/customers/CustomerTableCellItem';
import CustomerEmailTableCellItem from 'cpmCarparks/assets/customers/CustomerEmailTableCellItem';
import _get from 'lodash/get';

const CustomerParkings = ({
  customer, timezone, parkingCanBeAdded, onAddParking,
}) => {
  // Get parkings (type: regular or assigned) and only with status: "in_progress" or "started"
  const parkings = mParking.getParkingsRegularOrAssigned(customer.parkings);

  // Permissions
  const {
    pCpm: { parkingWrite },
  } = permissions;
  if (!_isEmpty(parkings)) {
    return (
      <div className="d-flex align-items-center">
        {_map(parkings, (parking) => (
          <React.Fragment key={parking.id}>
            <ParkingStatusInfoIcon timezone={timezone} customer={customer} parking={parking} withInfoTooltip />
            {/* Show tooltip only for assigned space */}
            {mParking.isAssigned(parking) && (
              <Tooltip content={<LocalizedMessage id={_get(mParking.type(parking), 'labelId', 'null')} />}>
                <AppIcon id={mParking.type(parking).icon} color={_get(mParking.type(parking), 'color', '')} />
              </Tooltip>
            )}
          </React.Fragment>
        ))}
      </div>
    );
  }
  if (parkingCanBeAdded) {
    return (
      <OnlyWith
        permissions={{
          needed: parkingWrite,
          permissionsOrContext: 'carparkPermissions',
        }}
      >
        <div className="d-flex align-items-center">
          <AddRightIconButton tooltipId="tooltip.addAccessRight" onClick={onAddParking} />
        </div>
      </OnlyWith>
    );
  }
  return null;
};

CustomerParkings.propTypes = {
  customer: cpmCustomerDataShape.isRequired,
  timezone: PropTypes.string.isRequired,
  parkingCanBeAdded: PropTypes.bool.isRequired,
  onAddParking: PropTypes.func.isRequired,
};

const CustomerBookings = ({
  customer, timezone, parkingCanBeAdded, onAddBooking,
}) => {
  // Permissions
  const {
    pCpm: { bookingWrite },
  } = permissions;
  // Get bookings only with status: "in_progress" or "started"
  const bookings = mParking.areStartedOrInProgress(customer.bookings);

  if (!_isEmpty(bookings)) {
    return (
      <div className="d-flex align-items-center">
        {_map(bookings, (booking) => (
          <React.Fragment key={booking.id}>
            <BookingStatusInfoIcon timezone={timezone} customer={customer} booking={booking} withInfoTooltip />
          </React.Fragment>
        ))}
      </div>
    );
  }
  if (parkingCanBeAdded) {
    return (
      <OnlyWith
        permissions={{
          needed: bookingWrite,
          permissionsOrContext: 'carparkPermissions',
        }}
      >
        <div className="d-flex align-items-center">
          <AddBookingIconButton tooltipId="tooltip.addBooking" onClick={onAddBooking} />
        </div>
      </OnlyWith>
    );
  }
  return null;
};

CustomerBookings.propTypes = {
  customer: cpmCustomerDataShape.isRequired,
  timezone: PropTypes.string.isRequired,
  parkingCanBeAdded: PropTypes.bool.isRequired,
  onAddBooking: PropTypes.func.isRequired,
};

const CustomerAccount = ({
  customer, onCreateAccount, onDeleteAccount, isOnDemand, withAccountActions,
}) => {
  const {
    labelId: statusLabelId,
    icon: statusIcon,
    color: statusColor,
    tooltipId: statusTooltipId,
    active: statusActive,
  } = mCustomer.accountStatus(customer.account);
  const {
    pCpm: { customerWrite },
  } = permissions;

  return !isOnDemand ? (
    <div className="d-flex align-items-center">
      <Chip
        backgroundColor={statusColor}
        size="medium"
        width="100px"
        label={(
          <div
            className="d-flex align-items-center justify-content-between"
            style={{ width: withAccountActions && 87 }}
          >
            <LocalizedMessage id={statusLabelId} />
            {withAccountActions && (
              <OnlyWith
                permissions={{
                  needed: customerWrite,
                  permissionsOrContext: 'carparkPermissions',
                }}
              >
                <div style={{ backgroundColor: 'white', borderRadius: 100 }}>
                  <IconButton
                    iconId={statusIcon}
                    size="small"
                    tooltipId={statusTooltipId}
                    onClick={() => (!statusActive ? onCreateAccount(customer) : onDeleteAccount(customer))}
                  />
                </div>
              </OnlyWith>
            )}
          </div>
        )}
      />
    </div>
  ) : null;
};

CustomerAccount.propTypes = {
  customer: cpmCustomerDataShape.isRequired,
  onCreateAccount: PropTypes.func,
  onDeleteAccount: PropTypes.func,
  isOnDemand: PropTypes.bool.isRequired,
  withAccountActions: PropTypes.bool.isRequired,
};

CustomerAccount.defaultProps = {
  onCreateAccount: null,
  onDeleteAccount: null,
};

const CustomerRow = React.forwardRef(
  (
    {
      timezone,
      customer,
      rowSpecs,
      withParkingAdd,
      withAccountActions,
      withColumnUser,
      withColumnUserEmail,
      onAddParking,
      onAddBooking,
      onEdit,
      onDelete,
      onCreateAccount,
      onDeleteAccount,
      withBooking,
      withParking,
    },
    ref,
  ) => {
    // Permissions
    const {
      pCpm: {
        parkingCategoryRead, customerWrite, parkingRead, bookingRead,
      },
    } = permissions;

    // On demand flag
    const isOnDemand = mCustomer.isOnDemand(customer);

    // Editable flag and tooltip
    const isEditable = !isOnDemand;
    let editTooltipId;
    if (isOnDemand) {
      editTooltipId = 'tooltip.cannotEditOnDemandUser';
    }

    // Flag to indicate if a parking can be added from here
    const parkingCanBeAdded = withParkingAdd && !isOnDemand;

    // Handlers
    const handleEdit = () => {
      onEdit(customer);
    };

    const handleDelete = () => {
      onDelete(customer);
    };

    const handleAddParking = () => {
      if (onAddParking) {
        onAddParking(customer);
      }
    };

    const handleAddBooking = () => {
      if (onAddBooking) {
        onAddBooking(customer);
      }
    };

    return (
      <TableRow
        ref={ref}
        element={customer}
        rowSpecs={rowSpecs}
        actions={[
          <OnlyWith
            key="bActions"
            permissions={{
              needed: customerWrite,
              permissionsOrContext: 'carparkPermissions',
            }}
          >
            <React.Fragment>
              <EditIconButton onClick={handleEdit} disabled={!isEditable} tooltipId={editTooltipId} />
              <DeleteIconButton onClick={handleDelete} />
            </React.Fragment>
          </OnlyWith>,
        ]}
      >
        {withColumnUser ? (
          <TableCell noPadding>
            <CustomerTableCellItem customer={customer} />
          </TableCell>
        ) : (
          <React.Fragment>
            <TableCell
              className={classnames({
                'table-first-details': rowSpecs.withDetails,
              })}
            >
              <TruncatedTooltipText>{customer.lastname}</TruncatedTooltipText>
            </TableCell>
            <TableCell className="hidden-md-down">
              <TruncatedTooltipText>{customer.firstname}</TruncatedTooltipText>
            </TableCell>
            <OnlyWith
              permissions={{
                needed: parkingCategoryRead,
                permissionsOrContext: 'carparkPermissions',
              }}
            >
              <TableCell className="hidden-xs-down">
                <ParkingCategoryDesc parkingCategoryId={mCustomer.getParkingCategoryId(customer)} />
              </TableCell>
            </OnlyWith>
          </React.Fragment>
        )}

        {withColumnUserEmail ? (
          <TableCell noPadding>
            <CustomerEmailTableCellItem customer={customer} />
          </TableCell>
        ) : null}

        <TableCell className="table-content-2 hidden-sm-down" noPadding>
          <CustomerAccount
            customer={customer}
            onCreateAccount={onCreateAccount}
            onDeleteAccount={onDeleteAccount}
            isOnDemand={isOnDemand}
            withAccountActions={withAccountActions}
          />
        </TableCell>
        <TableCell className="table-content-2 hidden-sm-down">
          <TruncatedTooltipText>{customer.spaceNumber}</TruncatedTooltipText>
        </TableCell>
        {withBooking && (
          <OnlyWith
            permissions={{
              needed: bookingRead,
              permissionsOrContext: 'carparkPermissions',
            }}
          >
            <TableCell className="table-content-2 table-content-center hidden-md-down" noPadding>
              <CustomerBookings
                customer={customer}
                timezone={timezone}
                parkingCanBeAdded={parkingCanBeAdded}
                onAddBooking={handleAddBooking}
              />
            </TableCell>
          </OnlyWith>
        )}
        {withParking && (
          <OnlyWith
            permissions={{
              needed: parkingRead,
              permissionsOrContext: 'carparkPermissions',
            }}
          >
            <TableCell className="table-content-2 table-content-center hidden-md-down" noPadding>
              <CustomerParkings
                customer={customer}
                timezone={timezone}
                parkingCanBeAdded={parkingCanBeAdded}
                onAddParking={handleAddParking}
              />
            </TableCell>
          </OnlyWith>
        )}
      </TableRow>
    );
  },
);

CustomerRow.displayName = 'CustomerRow';

CustomerRow.propTypes = {
  timezone: PropTypes.string.isRequired,
  customer: cpmCustomerDataShape.isRequired,
  rowSpecs: listRowSpecsShape.isRequired,
  withParkingAdd: PropTypes.bool,
  withAccountActions: PropTypes.bool,
  withBooking: PropTypes.bool,
  withParking: PropTypes.bool,
  withColumnUser: PropTypes.bool,
  withColumnUserEmail: PropTypes.bool,
  onAddParking: PropTypes.func,
  onAddBooking: PropTypes.func,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  onCreateAccount: PropTypes.func,
  onDeleteAccount: PropTypes.func,
};

CustomerRow.defaultProps = {
  withParkingAdd: true,
  withAccountActions: true,
  withBooking: true,
  withParking: true,
  withColumnUser: false,
  withColumnUserEmail: false,
  onAddParking: null,
  onAddBooking: null,
  onEdit: null,
  onDelete: null,
  onCreateAccount: null,
  onDeleteAccount: null,
};

module.exports = CustomerRow;



// WEBPACK FOOTER //
// ./routes/cpm/routes/carparks/assets/customers/CustomerRow.js